import { BannerItem } from '@athena/forge';
import withEventHelper from '../common/EventHelper';
import Banner from '../common/Banner';
import { getLocalizedString } from '../../utils/i18n-utils';
import MutedText from '../common/MutedText';
import { useWidgetContext } from '../context/WidgetContext';
import { useCallback, useEffect } from 'react';
import ButtonBar from '../common/ButtonBar';
import { PASSWORD_REQUIREMENTS } from '../../constants';


interface PasswordRequirementProps {
  isLengthValid?: boolean;
  isNumberPresent?: boolean;
  isLowerCasePresent?: boolean;
}
const calculatePasswordRequirementsState = (password?: string) => {
  const numberRegex = /\d/;
  const isLowerCasePresentRegex = /[a-z]/;

  let isLengthValid = false;
  let isNumberPresent = false;
  let isLowerCasePresent = false;
  if (password) {
    if (password.match(numberRegex)) {
      isNumberPresent = true;
    }
    if (
      password.length >= PASSWORD_REQUIREMENTS.LENGTH_LOWER_BOUND
      && password.length <= PASSWORD_REQUIREMENTS.LENGTH_HIGHER_BOUND
    ) {
      isLengthValid = true;
    }
    if (password.match(isLowerCasePresentRegex)) {
      isLowerCasePresent = true;
    }
  }
  return {
    isLengthValid,
    isNumberPresent,
    isLowerCasePresent,
  };
};

function PasswordRequirementsChecklist({ password }: { password?: string }) {
  const { eventEmitter } = useWidgetContext();
  const passwordRequirementsMetState: PasswordRequirementProps = calculatePasswordRequirementsState(password);
  const generateBannerItem = useCallback((message: string, isRequirementMet?: boolean) => {
    if (!isRequirementMet) {
      return (
        <BannerItem className="fe_u_padding--bottom-xsmall">
          <MutedText className="fe_u_margin--bottom-none password-requirements-checklist-item" text={message} />
        </BannerItem>
      );
    }
  }, []);
  const { isLengthValid, isLowerCasePresent, isNumberPresent } =
    passwordRequirementsMetState;
  const isPasswordRequirementsMet =
    isLengthValid && isLowerCasePresent && isNumberPresent;

  useEffect(() => {
    eventEmitter.emitEventToComponent({
      componentName: ButtonBar.displayName!,
      eventName: 'set-form-validation-state',
      eventPayload: { isFormValid: false },
    });
  }, [ eventEmitter ]);

  if (isPasswordRequirementsMet) {
    eventEmitter.emitEventToComponent({
      componentName: ButtonBar.displayName!,
      eventName: 'set-form-validation-state',
      eventPayload: { isFormValid: true },
    });
  }
  return (
    <div className="fe_u_margin--bottom-medium">
      {isPasswordRequirementsMet ? (
        <Banner alertType={'success'} headerText={getLocalizedString('password.requirements.checklist.header')}>
          <BannerItem>{getLocalizedString('password.requirement.fulfilled')}</BannerItem>
        </Banner>
      ) : (
        <Banner alertType={'info'} headerText={getLocalizedString('password.requirements.checklist.header')}>
          {generateBannerItem(getLocalizedString('password.requirement.length'), isLengthValid)}
          {generateBannerItem(getLocalizedString('password.requirement.lower'), isLowerCasePresent)}
          {generateBannerItem(getLocalizedString('password.requirement.number'), isNumberPresent)}
        </Banner>
      )}
    </div>
  );
}

PasswordRequirementsChecklist.displayName = 'PasswordRequirementsChecklist';
export default withEventHelper(PasswordRequirementsChecklist);
